import { Places } from '@bridebook/models/source/models/Places';
import type { IPlace } from '@bridebook/models/source/models/Places.types';
import type { GooglePlace } from '@bridebook/web/app-shared/lib/search/types';

// Maps a Firestore `IPlace` into the GooglePlace shape consumed by parseGooglePlace / getArea.
// Only fields these downstream helpers read are populated; geometry/vicinity/formatted_address
// are omitted on purpose (consumers that need them must fetch live Place Details).
const iPlaceToGooglePlace = (place: IPlace): GooglePlace => {
  const address_components = (place.components ?? []).map((c) => ({
    long_name: c.name?.long ?? '',
    short_name: c.name?.short ?? '',
    types: c.types ?? [],
  })) as google.maps.places.PlaceResult['address_components'];

  return {
    name: place.name,
    place_id: place.id,
    address_components,
  };
};

// Lets a caller skip a Google Place Details billing call when the place is already in our
// Firestore `places` collection (keyed by Google Place ID). Returns null on miss / error;
// caller falls back to `googlePlacesService.getPlaceDetails(...)`.
export const tryGetGooglePlaceFromFirestore = async (
  placeId: string,
): Promise<GooglePlace | null> => {
  try {
    const place = await Places._.getById(placeId).get();
    const googlePlace = iPlaceToGooglePlace(place);

    // Country-level entries are cached with only a single country/political component,
    // missing values downstream helpers need — force a live Place Details fetch instead.
    const components = googlePlace.address_components ?? [];
    if (
      components.length === 1 &&
      components[0].types.includes('country') &&
      components[0].types.includes('political')
    ) {
      return null;
    }

    return googlePlace;
  } catch {
    return null;
  }
};
